/**
 * HeroHome
 * - Keeps the cover image visible until a real video frame is rendered.
 * - Video loads natively via `src` (no lazy-loading — hero is always above the fold).
 */
class HeroHome {

	/**
	 * Create instance.
	 */
	constructor() {

		this.selector = '.b-hero-home';
		this.listeners = new WeakMap();

		if ( document.readyState === 'loading' ) {

			document.addEventListener( 'DOMContentLoaded', () => this.init(), { once: true } );

		} else {

			this.init();

		}

	}

	/**
	 * Process all hero blocks present in the document.
	 *
	 * @return {void}
	 */
	init() {

		document.querySelectorAll( this.selector ).forEach( ( block ) => this.setupBlock( block ) );

	}

	/**
	 * Setup a single hero block.
	 *
	 * @param {Element} block Hero block.
	 * @return {void}
	 */
	setupBlock( block ) {

		const video = block.querySelector( '.b-hero-home__background-video' );
		const cover = block.querySelector( '.b-hero-home__video-cover' );

		if ( !video || !cover ) {

			return;

		}

		this.attachListeners( video, cover );

		// Handle a video that started playing before this script ran.
		this.revealIfAlreadyPlaying( video, cover );

	}

	/**
	 * Attach event listeners once per video element.
	 *
	 * @param {HTMLVideoElement} video Video element.
	 * @param {Element} cover Cover element.
	 * @return {void}
	 */
	attachListeners( video, cover ) {

		if ( this.listeners.has( video ) ) {

			return;

		}

		/**
		 * Handle the playing event.
		 * @return {void}
		 */
		const playingHandler = () => this.revealOnNextRenderedFrame( video, cover );
		/**
		 * Handle the loadeddata event.
		 * @return {void}
		 */
		const loadedDataHandler = () => this.revealIfAlreadyPlaying( video, cover );
		/**
		 * Handle the pause event.
		 * @return {void}
		 */
		const pauseHandler = () => this.showCover( cover );
		/**
		 * Handle the ended event.
		 * @return {void}
		 */
		const endedHandler = () => this.showCover( cover );
		/**
		 * Handle the error event.
		 * @return {void}
		 */
		const errorHandler = () => this.showCover( cover );

		video.addEventListener( 'playing', playingHandler );
		video.addEventListener( 'loadeddata', loadedDataHandler );
		video.addEventListener( 'pause', pauseHandler );
		video.addEventListener( 'ended', endedHandler );
		video.addEventListener( 'error', errorHandler );

		this.listeners.set( video, {
			playingHandler,
			loadedDataHandler,
			pauseHandler,
			endedHandler,
			errorHandler,
		} );

	}

	/**
	 * Reveal the video only after a real frame reaches the rendering pipeline.
	 *
	 * `playing` fires when the browser has enough data to start playback — it
	 * does not mean the whole file is buffered, which is exactly what is needed
	 * for a progressive background video.
	 *
	 * @param {HTMLVideoElement} video Video element.
	 * @param {Element} cover Cover element.
	 * @return {void}
	 */
	revealOnNextRenderedFrame( video, cover ) {

		if ( video.paused || video.ended || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA ) {

			return;

		}

		/** Reveal the cover after a frame is rendered. @return {void} */
		const reveal = () => {

			if ( !video.paused && !video.ended && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA ) {

				this.hideCover( cover );

			}

		};

		// `requestVideoFrameCallback` is the safest point: the browser confirms
		// that a decoded frame reached the compositor.
		if ( typeof video.requestVideoFrameCallback === 'function' ) {

			video.requestVideoFrameCallback( reveal );

		} else {

			requestAnimationFrame( reveal );

		}

	}

	/**
	 * Handle a video that was already playing before listeners were attached.
	 *
	 * @param {HTMLVideoElement} video Video element.
	 * @param {Element} cover Cover element.
	 * @return {void}
	 */
	revealIfAlreadyPlaying( video, cover ) {

		if ( !video.paused && !video.ended ) {

			this.revealOnNextRenderedFrame( video, cover );

		}

	}

	/**
	 * Hide the cover once a real video frame is ready.
	 *
	 * @param {Element} cover Cover element.
	 * @return {void}
	 */
	hideCover( cover ) {

		cover.classList.add( 'is-hidden' );

	}

	/**
	 * Show the cover when playback is paused, ended, or failed.
	 *
	 * @param {Element} cover Cover element.
	 * @return {void}
	 */
	showCover( cover ) {

		cover.classList.remove( 'is-hidden' );

	}

}

new HeroHome();
